import { StrictMode, useEffect } from "react";
import { createRoot } from "react-dom/client";
import {
  BrowserRouter,
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from "react-router-dom";
import * as Sentry from "@sentry/react";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/lib/api/queryClient";
import App from "@/App";
import { AuthProvider } from "@/lib/auth/AuthContext";
import { ThemeProvider } from "@/lib/theme/ThemeProvider";
import { ThemeSync } from "@/lib/theme/ThemeSync";
import { I18nProvider } from "@/lib/i18n/I18nProvider";
import { LocaleSync } from "@/lib/i18n/LocaleSync";
import { ToastProvider } from "@/components/Toast/Toast";
import { BackgroundJobsProvider } from "@/providers/BackgroundJobsProvider";
import { PBackgroundJobs } from "@/components/PBackgroundJobs/PBackgroundJobs";
import { AiBackgroundJobBridge } from "@/components/AiAssistant/AiBackgroundJobBridge";
import { AiDataSyncBridge } from "@/components/AiAssistant/AiDataSyncBridge";
import { SearchHotkey } from "@/lib/a11y/SearchHotkey";
import { ConnectionWatcher } from "@/lib/connection/ConnectionWatcher";
import { bootstrapRive } from "@/lib/rive/bootstrapRive";
import {
  installChunkErrorReload,
  isStaleChunkError,
} from "@/lib/chunkReload";
import { installKeyboardScrollIntoView } from "@/lib/layout/keyboardScroll";
import { sentryScrubIntegration, sentryScrubOptions } from "@/lib/sentry";
import { pruneAdhocGuestIdOnce } from "@/lib/storage/pruneAdhocGuestId";
import { resetTableCacheOnce } from "@/lib/storage/resetTableCache";
import "@/styles/index.css";
import { NewVersionNotice } from "@/components/NewVersionNotice/NewVersionNotice";

// Auto-reload on stale chunk 404s from prior deploys. Installed before
// any lazy() route mounts so the listener is in place when the first
// dynamic import resolves.
installChunkErrorReload();

// Bring a focused field back into the space the software keyboard leaves. iOS
// tries to do this itself but can only scroll the PAGE, which never moves
// inside a `position: fixed` dialog — and every popup in this app is one.
installKeyboardScrollIntoView();

// Self-host the Rive WASM before any <VideoPlayerRiv/> mounts so the runtime
// never reaches out to unpkg/jsdelivr.
bootstrapRive();

// Drop `guestId` from the ad-hoc guest view snapshots that captured it while
// it was part of the default columns. Runs once, before any table mounts.
pruneAdhocGuestIdOnce();
// And clear the table cache once: the entry shape gained a base view + version,
// and the read side went from two surfaces to all of them, so every entry
// written before today is either unusable or an orphan nobody meant to activate.
// After this, `pruneAdhocGuestIdOnce` has nothing left to find — it is kept for
// the browsers that never ran either.
resetTableCacheOnce();

// Sentry stays inert when VITE_SENTRY_DSN is empty (local dev / OSS).
// EU instance only — DSN must contain `de.sentry.io` or `ingest.eu`.
if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_SENTRY_ENV ?? import.meta.env.MODE,
    release: import.meta.env.VITE_BUILD_VERSION,
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    sendDefaultPii: false,
    // NOTE: user feedback (Sentry.captureFeedback from a bug report) is a
    // SEPARATE envelope and does NOT pass through `beforeSend` below — the
    // scrub only applies to error events, and no beforeSendFeedback hook is
    // supported as a top-level option in this SDK version. That is why
    // SupportClient.reportBug sends the message ONLY: since 2026-08-14 it no
    // longer attaches the reporter's name/email, because nothing here could
    // have stripped them (IT-29). Since K4-B the integration below reaches
    // feedback too (processEvent runs for every event type) — it takes the
    // page url's query; the message itself is still the only thing sent.
    //
    // beforeSend / beforeBreadcrumb / beforeSendTransaction / beforeSendSpan:
    // the free-text scrub (Kanzlei K4-B). beforeSendSpan is the only hook an
    // INP span passes — its name is the clicked element's path. The spread
    // also sets maxValueLength 8192: the SDK cuts texts to 250 characters
    // before any hook runs, and a cut text loses the suffix the patterns
    // anchor on. Nothing after the spread may set one of its keys
    // (sentry.wiring.test.ts).
    ...sentryScrubOptions(),
    integrations: [
      sentryScrubIntegration(),
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
  });
}

// OnboardingProvider is intentionally NOT wrapped here — its useEffect calls an
// authenticated endpoint on mount, so it can only live inside <PrivateRoute>
// (mounted from `AppShell`). BackgroundJobsProvider is fine at root: it only
// polls when there are jobs in localStorage (none for anonymous users).

function AppCrash({ error }: { error: unknown }) {
  // Last-resort: if a chunk error reached the boundary anyway (listener
  // attached too late, or path that bypassed Vite's preload chain),
  // reload from here. Tiny defer so the boundary mount doesn't race the
  // reload.
  useEffect(() => {
    if (!isStaleChunkError(error)) return;
    const t = setTimeout(() => window.location.reload(), 50);
    return () => clearTimeout(t);
  }, [error]);

  return (
    <div role="alert" style={{ padding: 24, fontFamily: "system-ui" }}>
      <h1>Something went wrong.</h1>
      <p>The page failed to load.</p>
      <button onClick={() => window.location.reload()}>Refresh</button>
    </div>
  );
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={({ error }) => <AppCrash error={error} />}>
      <ThemeProvider>
        <I18nProvider>
          <QueryClientProvider client={queryClient}>
            <BrowserRouter>
              <AuthProvider>
                <ThemeSync />
                <LocaleSync />
                <BackgroundJobsProvider>
                  <ToastProvider>
                    <App />
                    {/* Mounted once at root so navigation between dashboard ↔ event
                        routes doesn't unmount + remount the panel (the per-shell
                        mounts used to blink + reset state on every navigation). */}
                    <PBackgroundJobs />
                    {/* Registers AI-submitted server-side jobs (export / bulk
                        send) with the tracker above — see AiBackgroundJobBridge. */}
                    <AiBackgroundJobBridge />
                    {/* Invalidates react-query data when an AI server tool
                        mutates it, so query-backed pages live-update — see
                        AiDataSyncBridge / lib/ai-sync. */}
                    <AiDataSyncBridge />
                    {/* Cmd/Ctrl+F → focuses the search box of the current
                        view. One listener at the root, not one per shell. */}
                    <SearchHotkey />
                    {/* Feeds the app-bar connection dot: browser online/offline
                        events plus a sparse authenticated heartbeat — see
                        lib/connection. Needs AuthProvider, hence this spot. */}
                    <ConnectionWatcher />
                    {/* Offers a reload when a newer build is deployed —
                        builder tabs stay open for days (lib/version). */}
                    <NewVersionNotice />
                  </ToastProvider>
                </BackgroundJobsProvider>
              </AuthProvider>
            </BrowserRouter>
          </QueryClientProvider>
        </I18nProvider>
      </ThemeProvider>
    </Sentry.ErrorBoundary>
  </StrictMode>,
);
